import { MerchantCustomerNotifierType } from "@/models/club";
import MessageText, { smsPartsOf } from "@/modules/marketing/MessageText";
import useChangeCustomerNotifier from "@/services/autoMessage/hooks/useChangeCustomerNotifier";
import { MerchantCustomerNotifier } from "@/services/autoMessage/response";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import useAutoMessageStore from "@/store/autoMessage";
import { Button, Loader, Switch } from "@mantine/core";
import { IconBell, IconCalendarCheck, IconPencil, IconUserPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import classes from "./autoMessage.module.css";

type Props = { config: MerchantCustomerNotifier };

const fa = (value: number) => value.toLocaleString("fa-IR");

// What each automatic SMS is, in the salon's words.
const copy: Record<number, { title: string; description: string; icon: typeof IconBell }> = {
  [MerchantCustomerNotifierType.Value0]: {
    title: "خوش‌آمدگویی به مشتری جدید",
    description: "وقتی مشتری تازه‌ای ثبت می‌شود، یک پیامک گرم برایش فرستاده می‌شود.",
    icon: IconUserPlus,
  },
  [MerchantCustomerNotifierType.Value1]: {
    title: "پیامک بعد از ثبت نوبت",
    description: "بعد از ثبت نوبت یا فاکتور برای مشتری فرستاده می‌شود و می‌تواند تاریخ و ساعت نوبت را داشته باشد.",
    icon: IconCalendarCheck,
  },
  [MerchantCustomerNotifierType.Value2]: {
    title: "خبر نوبت آنلاین برای سالن",
    description: "با هر نوبت آنلاین، پیامکی با نام مشتری و زمان نوبت برای خود سالن فرستاده می‌شود.",
    icon: IconBell,
  },
};

function SendAutoMessage({ config }: Props) {
  return config.type === MerchantCustomerNotifierType.Value2 ? <MerchantNotificationCard config={config} /> : <CustomerAutoMessage config={config} />;
}

function CardShell({ config, active, loading, onToggle, children }: { config: MerchantCustomerNotifier; active: boolean; loading: boolean; onToggle: (checked: boolean) => void; children?: React.ReactNode }) {
  const info = copy[config.type ?? 0] ?? { title: config.typeDisplay ?? "پیامک خودکار", description: "", icon: IconBell };
  const Icon = info.icon;
  return (
    <article className={classes.card} data-active={active}>
      <div className={classes.head}>
        <span className={classes.icon} aria-hidden="true"><Icon size={22} stroke={1.7} /></span>
        <div className={classes.titleBlock}>
          <h3>{info.title}</h3>
          <span className={classes.status}>{active ? "فعال" : "خاموش"}</span>
        </div>
        {loading ? <Loader size="xs" color="violet" /> : (
          <Switch aria-label={info.title} checked={active} color="violet" size="md" onChange={(event) => onToggle(event.currentTarget.checked)} />
        )}
      </div>
      <p className={classes.description}>{info.description}</p>
      {children}
    </article>
  );
}

function CustomerAutoMessage({ config }: Props) {
  const tags = useGetSmsPlaceHolder().data ?? [];
  const { setNotifier } = useAutoMessageStore();
  const { changeCustomerNotifierMerchant, isLoading } = useChangeCustomerNotifier();
  const navigate = useNavigate();
  const text = config.smsTemplate?.text ?? "";
  const active = !!config.isActive;

  const edit = () => {
    setNotifier({ notifier: config });
    navigate("selectTemplate");
  };
  // Turning on goes through choosing the text and time; turning off keeps both for next time.
  const toggle = (checked: boolean) => {
    if (checked) edit();
    else changeCustomerNotifierMerchant({ notifierType: config.type, sendTime: config.sendTime, isActive: false, smsTemplateId: config.smsTemplate?.id });
  };

  return (
    <CardShell config={config} active={active} loading={isLoading} onToggle={toggle}>
      {active && text ? (
        <>
          <div className={classes.bubble}><MessageText text={text} tags={tags} /></div>
          <div className={classes.footer}>
            <span className={classes.meta}>
              {config.sendTime ? `${fa(config.sendTime)} دقیقه بعد` : "بلافاصله"} · {fa(smsPartsOf(text, tags))} پیامک
            </span>
            <Button variant="subtle" color="violet" size="compact-sm" leftSection={<IconPencil size={15} />} onClick={edit}>ویرایش متن و زمان</Button>
          </div>
        </>
      ) : (
        <div className={classes.footer}>
          <span className={classes.meta}>برای روشن کردن، متن و زمان ارسال را انتخاب کنید.</span>
          <Button variant="light" color="violet" size="compact-sm" onClick={edit}>انتخاب متن</Button>
        </div>
      )}
    </CardShell>
  );
}

function MerchantNotificationCard({ config }: Props) {
  const { changeCustomerNotifierMerchant, isLoading } = useChangeCustomerNotifier();
  return (
    <CardShell
      config={config}
      active={!!config.isActive}
      loading={isLoading}
      onToggle={(checked) => changeCustomerNotifierMerchant({ notifierType: MerchantCustomerNotifierType.Value2, isActive: checked })}
    >
      <div className={classes.bubble}>نوبت جدید توسط مریم رضایی در تاریخ 1405/07/25 ساعت 16:30 از طریق سایت ثبت شد.</div>
    </CardShell>
  );
}

export default SendAutoMessage;
